'use client';
import React, { useState } from 'react';
import { Loader2, Sparkles, ArrowRight } from 'lucide-react';
import ExpertCard, { ExpertCardData } from '@/components/ui/ExpertCard';
import { QueryFormData } from './QueryForm';
import { toast } from 'sonner';

interface MatchResultsProps {
  experts: ExpertCardData[] | null;
  loading: boolean;
  query: QueryFormData | null;
}

export default function MatchResults({ experts, loading, query }: MatchResultsProps) {
  const [initiating, setInitiating] = useState<string | null>(null);

  const handleInitiate = async (expertId: string, pseudonym: string) => {
    setInitiating(expertId);
    // Backend integration: POST /api/engagements { expert_id, query_domain, buyer_id }
    await new Promise((r) => setTimeout(r, 1000));
    setInitiating(null);
    toast.success(`Engagement initiated with ${pseudonym}`);
  };

  if (loading) {
    return (
      <div className="surface-elevated p-8 flex flex-col items-center gap-5 min-h-[400px] justify-center">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
          <Loader2 size={28} className="text-primary animate-spin" />
        </div>
        <div className="text-center">
          <p className="font-medium text-foreground mb-1">Matching experts…</p>
          <p className="text-sm text-muted-foreground">Ranking by rating, badge tier, and domain relevance</p>
        </div>
        <div className="flex flex-col gap-3 w-full max-w-sm">
          {[1, 2, 3].map((i) => (
            <div key={`skeleton-${i}`} className="animate-pulse flex items-center gap-3 p-3 rounded-lg bg-muted/30">
              <div className="w-10 h-10 rounded-full bg-muted flex-shrink-0" />
              <div className="flex-1 flex flex-col gap-2">
                <div className="h-3 bg-muted rounded w-2/3" />
                <div className="h-2 bg-muted rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!experts) {
    return (
      <div className="surface-elevated p-8 flex flex-col items-center gap-5 min-h-[400px] justify-center text-center">
        <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
          <Sparkles size={24} className="text-muted-foreground" />
        </div>
        <div>
          <h3 className="font-heading text-lg text-foreground mb-2">Matched Experts Appear Here</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            Fill in the query form and click "Find Matching Experts". The top 5 live experts ranked by rating and badge tier will appear here.
          </p>
        </div>
      </div>
    );
  }

  if (experts.length === 0) {
    return (
      <div className="surface-elevated p-8 flex flex-col items-center gap-5 min-h-[400px] justify-center text-center">
        <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
          <span className="text-2xl text-muted-foreground">◆</span>
        </div>
        <div>
          <h3 className="font-heading text-lg text-foreground mb-2">No Experts Found</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            No live experts match the selected domain right now. Try a broader domain or check back later as new experts are approved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg text-foreground">
            {experts.length} Expert{experts.length !== 1 ? 's' : ''} Matched
          </h2>
          {query && (
            <p className="text-xs text-muted-foreground font-mono-data mt-0.5">
              Domain: {query.domain} · {query.complexity} · {query.urgency}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-secondary font-mono-data">
          <Sparkles size={12} />
          Ranked by rating + badge tier
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {experts.map((expert, idx) => (
          <div key={`match-${expert.id}`} className="relative">
            {idx === 0 && (
              <div className="absolute -top-2 left-4 z-10">
                <span className="text-[10px] font-mono-data px-2 py-0.5 rounded-full bg-primary text-primary-foreground">
                  Best Match
                </span>
              </div>
            )}
            <div className={`surface-elevated p-4 flex items-start gap-4 ${idx === 0 ? 'border-primary/30 mt-2' : ''}`}>
              <div className="flex-shrink-0 w-6 flex items-center justify-center">
                <span className="font-mono-data text-xs text-muted-foreground font-semibold">#{idx + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <ExpertCard expert={expert} href="/expert-profile-page" />
              </div>
              <button
                onClick={() => handleInitiate(expert.id, expert.pseudonym)}
                disabled={initiating === expert.id}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 active:scale-95 transition-all duration-150 disabled:opacity-60 whitespace-nowrap"
                aria-label={`Initiate engagement with ${expert.pseudonym}`}
              >
                {initiating === expert.id ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <>
                    Initiate
                    <ArrowRight size={12} />
                  </>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-muted-foreground text-center font-mono-data pt-2">
        All profiles are identity-masked. Initiating an engagement does not reveal expert identity.
      </p>
    </div>
  );
}